import type { AiChatTaskStatusReq, AiChatTaskStatusRsp } from '@/api/ai-chat';
import type {
  AiChatSourceRef,
  ChatSessionTraceItem,
  ReplaySourceBlock,
} from '@/api/ai-chat/types';

import type { SessionClarification, SessionMessage } from '../util/message';

export type ChatApiStrategyKey = 'optimization' | 'agent';

export const DEFAULT_CHAT_API_STRATEGY_KEY: ChatApiStrategyKey = 'optimization';

/** 已上传附件。有文件时写入 input.items 的 mixed.media，不放 extra。 */
export interface ChatAttachment {
  url: string;
  name: string;
  mime_type: string;
  size: number;
}

/** 策略把响应收成补丁；调用方只负责写回会话。 */
export interface ChatStreamUpdate {
  patch?: Partial<SessionMessage>;
  append?: SessionMessage;
  pollTaskId?: string;
  /** 本帧带上的会话 / 轮次 id。有变化才出现。 */
  cursor?: {
    sessionId?: string;
    turnId?: string;
  };
}

/** 续传前页面上已经渲染的内容。 */
export interface ChatResumeSeed {
  content: string;
  progress?: string;
  trace?: ChatSessionTraceItem[];
  clarifications?: SessionClarification[];
  /** 历史快照里已经按 index 收下的块。续写往这张表上接，不重放。 */
  blocks?: ReplaySourceBlock[];
  /** 续写前已经渲染的提案。后面的 proposal_status 要能把它标成已采纳。 */
  proposal?: SessionMessage['proposal'];
  /** 续写前已经收下的助手产物。后面的 attachment 事件往这份上合并。 */
  artifacts?: SessionMessage['artifacts'];
}

export interface ChatStrategySendParams {
  sessionId: string;
  appId: string;
  message: string;
  taskId?: string;
  /** 已上传附件。组装进 input.items[].media，不写 extra。 */
  attachments?: ChatAttachment[];
  signal?: AbortSignal;
  emit: (update: ChatStreamUpdate) => void;
  /** 有值时这次请求是断点续传，不带 input。 */
  turnId?: string;
  seed?: ChatResumeSeed;
  /** 仅新开一轮写入 input.source_ref；续传忽略。 */
  sourceRef?: AiChatSourceRef;
}

export interface ChatApiStrategy {
  key: ChatApiStrategyKey;
  label: string;
  send: (params: ChatStrategySendParams) => Promise<void>;
  getTaskStatus: (
    params: AiChatTaskStatusReq,
    signal?: AbortSignal
  ) => Promise<AiChatTaskStatusRsp>;
}
